import { styled } from 'styled-components';

import { Box } from '#/components/Box/Box';
import { Button } from '#/components/Button/Button';
import { Tooltip } from '#/components/Tooltip/Tooltip';
import { useI18n } from '#/i18n/hooks/useI18n';
import type { SearchType } from '#/modules/search/types/SearchType';
import { trackClicked } from '#/utils/analytics/track/trackClicked';
import { trackEventOnce } from '#/utils/analytics/track/trackEventOnce';
import { LISTING_TYPE_PLURAL_HUMAN_NAME_MAP } from '#/utils/constants/general/listingTypePluralHumanNameMap';
import { invariant } from '#/utils/invariant';

type Props = {
  disabled?: boolean;
  listingType: SearchType;
  tabIndex?: number;

  // Save listing
  listingSaved: boolean;
  onSaveListing: () => Promise<void>;
  onUnsaveListing: () => Promise<void>;

  // Save Org
  orgDetails: {
    shouldShowSaveOrgTooltip: boolean;
    onSaveOrg: () => Promise<void>;
    onDismissSaveOrgTooltip: () => void;
  } | null;
};

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-around;
`;

export function SaveListingButton({
  disabled,
  listingType,
  listingSaved,
  onSaveListing,
  onUnsaveListing,
  orgDetails,
  tabIndex,
}: Props) {
  const { i18n } = useI18n();
  const buttonText = listingSaved ? i18n('Unsave') : i18n('Save');

  const onConfirm = async () => {
    invariant(orgDetails, 'Missing data to save org');

    trackClicked('Save Org Tooltip', { cta_type: 'yes' });
    await orgDetails.onSaveOrg();
  };

  const onDecline = () => {
    invariant(orgDetails, 'Missing data to save org');

    orgDetails.onDismissSaveOrgTooltip();
    trackClicked('Save Org Tooltip', { cta_type: 'no' });
  };

  return (
    <Tooltip
      content={
        <div data-qa-id="save-listing-tooltip">
          <Box mb={3}>
            {i18n(
              'Would you like to get alerts when this organization posts new %(listingTypeName)s?',
              {
                listingTypeName:
                  LISTING_TYPE_PLURAL_HUMAN_NAME_MAP(i18n)[listingType],
              },
            )}
          </Box>

          <ButtonContainer>
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                onDecline();
              }}
              qaId="decline-button"
            >
              {i18n('No, thank you')}
            </Button>

            <Button
              type="button"
              variant="primary"
              onClick={() => {
                void onConfirm();
              }}
              qaId="confirm-button"
            >
              {i18n('Yes!')}
            </Button>
          </ButtonContainer>
        </div>
      }
      placement="bottom-left"
      closeable
      size="large"
      variant="brand-blue"
      renderCondition={Boolean(orgDetails?.shouldShowSaveOrgTooltip)}
      onRender={() => trackEventOnce('Displayed: Save Org Tooltip')}
    >
      <div>
        <Button
          type="button"
          disabled={disabled}
          aria-label={buttonText}
          variant="secondary"
          icon={{ name: 'bookmark', size: 16 }}
          qaId={listingSaved ? 'unsave-listing' : 'save-listing'}
          onClick={() => {
            if (listingSaved) {
              void onUnsaveListing();
            } else {
              void onSaveListing();
            }
          }}
          tabIndex={tabIndex}
          title={buttonText}
        >
          {buttonText}
        </Button>
      </div>
    </Tooltip>
  );
}
